import { useMemo } from 'react';
import type { RoutePreview } from '../../types';

interface RoutePreviewSvgProps {
  preview: RoutePreview;
  width?: number;
  height?: number;
  strokeColor?: string;
  strokeWidth?: number;
  className?: string;
}

export function RoutePreviewSvg({
  preview,
  width = 80,
  height = 60,
  strokeColor = '#00d2be',
  strokeWidth = 2,
  className = '',
}: RoutePreviewSvgProps) {
  const pathData = useMemo(() => {
    const { coordinates, bounds } = preview;

    if (!coordinates || coordinates.length < 2) {
      return '';
    }

    const padding = 6;
    const viewWidth = width - padding * 2;
    const viewHeight = height - padding * 2;

    const lngRange = bounds.maxLng - bounds.minLng || 0.0001;
    const latRange = bounds.maxLat - bounds.minLat || 0.0001;

    // Maintain aspect ratio
    const scaleX = viewWidth / lngRange;
    const scaleY = viewHeight / latRange;
    const scale = Math.min(scaleX, scaleY);

    // Center the route
    const scaledWidth = lngRange * scale;
    const scaledHeight = latRange * scale;
    const offsetX = padding + (viewWidth - scaledWidth) / 2;
    const offsetY = padding + (viewHeight - scaledHeight) / 2;

    // Convert geo coords to SVG coords (flip Y axis)
    const toSvgX = (lng: number) => offsetX + (lng - bounds.minLng) * scale;
    const toSvgY = (lat: number) =>
      height - (offsetY + (lat - bounds.minLat) * scale);

    // Generate path
    const points = coordinates.map(
      ([lng, lat]) => `${toSvgX(lng).toFixed(2)},${toSvgY(lat).toFixed(2)}`
    );

    return `M${points.join(' L')}`;
  }, [preview, width, height]);

  if (!pathData) {
    return null;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
    >
      <path
        d={pathData}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.9}
      />
    </svg>
  );
}
